/**
 * The boot screen, and the recovery banner that outranks it.
 *
 * It reports what it is waiting for — enrolment, catalogue, server — rather
 * than a spinner that could mean any of them. And it is where an unacknowledged
 * outbox entry surfaces, which is the one thing here that must never be
 * dismissable without a resolution.
 */
import { Wordmark } from '@/components/brand'
import { Button, Card, Notice, Spinner } from '@/components/ui'
import { useT } from '@/i18n'
import { needsAPerson } from '@/outbox/outbox'
import { useSession } from '@/store/session'

export function Boot({
  state,
  onRetry
}: {
  state: 'connecting' | 'menu' | 'unenrolled' | 'no-cache'
  onRetry: () => void
}) {
  const t = useT()

  const message =
    state === 'connecting'
      ? t('boot.connecting')
      : state === 'menu'
        ? t('boot.loadingMenu')
        : state === 'unenrolled'
          ? t('boot.notEnrolled')
          : t('boot.noCache')

  return (
    <div className="flex h-full flex-col items-center justify-center gap-8 p-10 text-center">
      <Wordmark className="w-[min(20rem,60%)]" />

      {state === 'connecting' || state === 'menu' ? (
        <Spinner label={message} />
      ) : (
        <Card className="max-w-lg space-y-5 p-8">
          <p className="text-[length:var(--text-kiosk-lg)] font-semibold">{message}</p>
          <Button onClick={onRetry}>{t('boot.retry')}</Button>
        </Card>
      )}
    </div>
  )
}

/**
 * Money this kiosk took, or may have taken, that the server has not
 * acknowledged.
 *
 * Not dismissable. Ordinary claims replay automatically; the two states that
 * need a person do not, and this is where that person acts. Before, the entries
 * wearing the red banner were exactly the ones `replayOutbox` skipped, so the
 * only control on screen — "Send again" — did nothing for them and they stayed
 * for ever, unactionable, until somebody cleared IndexedDB.
 */
export function RecoveryBanner({
  onRetry,
  retrying,
  onResolve
}: {
  onRetry: () => void
  retrying: boolean
  /** Opens the attendant's resolve flow for one stuck claim. */
  onResolve: (id: string) => void
}) {
  const t = useT()
  const outbox = useSession((s) => s.outbox)
  if (outbox.length === 0) return null

  const total = outbox.reduce((sum, entry) => sum + BigInt(entry.amount), 0n)
  const stuck = outbox.filter(needsAPerson)
  const unresolved = stuck.filter((e) => e.state === 'unresolved')

  return (
    <div className="p-4">
      <Notice
        tone={stuck.length > 0 ? 'bad' : 'warn'}
        action={
          <Button variant="ghost" onClick={onRetry} disabled={retrying}>
            {t('recovery.retry')}
          </Button>
        }
      >
        <strong>{unresolved.length > 0 ? t('recovery.unresolved') : t('recovery.title')}</strong> —{' '}
        {unresolved.length > 0 ? t('recovery.unresolvedBody') : t('recovery.body')}{' '}
        <span className="tabular-nums">
          {outbox.length} × {(total / 100n).toString()}₮
        </span>
        {stuck.length > 0 && (
          <ul className="mt-3 space-y-2">
            {stuck.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center gap-2">
                <span className="tabular-nums">
                  {entry.reference ?? entry.id} — {(BigInt(entry.amount) / 100n).toString()}₮
                </span>
                <span className="text-[var(--color-ink-soft)]">
                  {entry.state === 'unresolved' ? t('recovery.unresolved') : entry.rejection}
                </span>
                {/* The affordance the banner never had. */}
                <Button variant="ghost" onClick={() => onResolve(entry.id)}>
                  {t('settings.title')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Notice>
    </div>
  )
}
